import { useStore } from "vuex";
import useMapAndStore from "@/lib/compositional-logic/useMapAndStore";
import { useRouter } from "vue-router";
import { RepositoryFactory, TemplateRepository } from "../https";
import useSignFunctions from "@/lib/compositional-logic/useSignFunctions";
import { useI18n } from "vue-i18n";
import useInputVars from "@/lib/compositional-logic/useInputVars";

const { getContractsFullInformation } =
  RepositoryFactory.getRepository<TemplateRepository>(TemplateRepository);

const {
  mapLocalBasicInformation,
  mapLocalDetailInformation,
  mapCloudFileToLocalFile,
  mapLocalSignerFromCloudSigner,
  mapLocalSecurityFromCloud,
  mapLocalViewerFromCloudViewer,
  mapLocalNotification,
  parseContractDate,
} = useMapAndStore();

const { convertTransactionAmount } = useInputVars();

export default function useContractFromTemplate(): {
  createContractFromTemplate: (
    templateId: string,
    folder: any
  ) => Promise<void>;
  loadContractFromContractId: (contractId: string) => Promise<void>;
  loadContractDetailInfo: (contractDetailInfo: any) => Promise<void>;
} {
  const store = useStore();
  const router = useRouter();
  const { getAccessCode } = useSignFunctions();
  const { t } = useI18n();

  const createContractFromTemplate = async (
    templateId: string,
    { folderId = "", folderTitle = "" } = {}
  ) => {
    const templateInformation = (await getContractsFullInformation(
      templateId
    )) as any;

    const localBasicContractInformation =
      mapLocalBasicInformation(templateInformation);
    const localDetailContractInformation = mapLocalDetailInformation(
      templateInformation.contractInformation ?? {}
    );
    const localFiles = mapCloudFileToLocalFile(
      templateInformation.contractFiles ?? []
    );
    const localSigners = mapLocalSignerFromCloudSigner(
      templateInformation.contractSigners ?? []
    );
    const localSecurity = mapLocalSecurityFromCloud(templateInformation);
    const localViewers = mapLocalViewerFromCloudViewer(
      templateInformation.contractViewers ?? []
    );

    const localCustomFields = templateInformation.contractCustomFields
      ? templateInformation.contractCustomFields
          .filter((item: any) => item.title)
          .map((item: any) => ({
            order: item.order,
            title: item.title,
            value: item.value,
          }))
      : [];

    await Promise.all([
      store.dispatch(
        "template/setTemplateBasicInfo",
        localBasicContractInformation
      ),
      store.dispatch(
        "template/setTemplateDetailInfo",
        localDetailContractInformation
      ),
      store.dispatch("template/setFolder", {
        folderId: folderId,
        folderName: folderTitle,
      }),
      store.dispatch("template/setFiles", localFiles),
      store.dispatch("template/setIsTemplate", false),
      store.dispatch("template/setSigners", localSigners),
      store.dispatch("template/setSecurity", localSecurity),
      store.dispatch("template/setViewers", localViewers),
      store.dispatch("template/setCreatedFromTemplate", templateId),
      store.dispatch("template/setCustomFields", localCustomFields),
    ]);

    router.push({ name: "Contract" });
  };

  const loadContractFromContractId = async (contractId: string) => {
    const accessCode = getAccessCode(contractId);
      
    const contractInformation = (await getContractsFullInformation(
      contractId,
      accessCode
    )) as any;

    const localBasicContractInformation =
      mapLocalBasicInformation(contractInformation);
    const localDetailContractInformation = mapLocalDetailInformation(
      contractInformation.contractInformation ?? {}
    );
    const localFiles = mapCloudFileToLocalFile(
      contractInformation.contractFiles ?? []
    );
    const localSigners = mapLocalSignerFromCloudSigner(
      contractInformation.contractSigners ?? []
    );
    const localSecurity = mapLocalSecurityFromCloud(contractInformation);
    const localViewers = mapLocalViewerFromCloudViewer(
      contractInformation.contractViewers ?? []
    );

    const localNotification = mapLocalNotification(
      contractInformation.notification ?? {}
    );

    const localCustomFields = contractInformation.contractCustomFields
      ? contractInformation.contractCustomFields
          .filter((item: any) => item.title)
          .map((item: any) => ({
            order: item.order,
            title: item.title,
            value: item.value,
          }))
      : [];

    await Promise.all([
      store.dispatch(
        "template/setTemplateBasicInfo",
        localBasicContractInformation
      ),
      store.dispatch("template/setId", contractId),
      store.dispatch(
        "template/setTemplateDetailInfo",
        localDetailContractInformation
      ),
      store.dispatch("template/setFiles", localFiles),
      store.dispatch("template/setIsTemplate", false),
      store.dispatch("template/setSigners", localSigners),
      store.dispatch("template/setSecurity", localSecurity),
      store.dispatch("template/setViewers", localViewers),
      store.dispatch(
        "template/setMessage",
        contractInformation.message ?? ""
      ),
      store.dispatch("template/setNotification", localNotification),

      store.dispatch(
        "template/setCustomFields",
        contractInformation.contractCustomFields ?? []
      ),
      store.dispatch(
        "template/setStatus",
        contractInformation.status ? contractInformation.status : 0
      ),
      store.dispatch(
        "template/setCountSigned",
        contractInformation.countSigned ? contractInformation.countSigned : 0
      ),
      store.dispatch("template/setFolder", {
        folderId: contractInformation.folderId ?? "",
        folderName: contractInformation.folderTitle ?? t("folder.allFolder"),
      }),
      store.dispatch("template/setCustomFields", localCustomFields),
    ]);
  };

  const loadContractDetailInfo = (contractDetailInfo: any) => {
    const contractDetailInfoData = {} as any;
    if (!contractDetailInfo) return contractDetailInfoData
    const {
      contractOverview,
      contractConclusionDate,
      contractEndDate,
      contractStartDate,
      isAutomaticallyUpdated,
      controlNumber,
      freeText,
      terminationNoticePeriod,
      transactionAmount,
    } = contractDetailInfo;

    contractDetailInfoData.contractOverview = contractOverview ?? "";
    contractDetailInfoData.contractConclusionDate = parseContractDate(
      contractConclusionDate
    ) as any;
    contractDetailInfoData.contractEndDate = parseContractDate(
      contractEndDate
    ) as any;
    contractDetailInfoData.contractStartDate = parseContractDate(
      contractStartDate
    ) as any;
    contractDetailInfoData.isAutomaticallyUpdated =
      String(isAutomaticallyUpdated) ?? "null";
    contractDetailInfoData.controlNumber = controlNumber ?? "";
    contractDetailInfoData.freeText = freeText ?? "";
    contractDetailInfoData.terminationNoticePeriod =
      terminationNoticePeriod ?? "";
    contractDetailInfoData.transactionAmount = transactionAmount.toString()
      ? convertTransactionAmount(transactionAmount.toString())
      : "";

    return contractDetailInfoData;
  };

  return {
    createContractFromTemplate,
    loadContractFromContractId,
    loadContractDetailInfo,
  };
}

